import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  TablePagination,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Search,
  FilterAltOff,
  Visibility,
  Close,
  CheckCircle,
  Cancel,
  DeleteOutline,
  Add,
  NotificationsActive,
  WarningAmber,
  FactCheck,
  Campaign,
  Tune,
} from '@mui/icons-material';
import {
  categoryPushNotificationsAPI,
  customProviderNotificationsAPI,
  providerNotificationsAPI,
  contactsAPI,
  categoriesAPI,
  eventsAPI,
  promotionsAPI,
} from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const dateObj = new Date(dateStr);
    if (isNaN(dateObj.getTime())) return dateStr;
    return dateObj.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

const getCategoryTitle = (cat) => {
  if (!cat) return '';
  return (
    cat.title_EN ||
    cat.title_en ||
    cat.title ||
    cat.Title ||
    cat.name ||
    cat.Name ||
    cat.categoryName ||
    cat.CategoryName ||
    cat.title_DE ||
    `Category #${cat.id}`
  );
};

const ProviderNotifications = () => {
  const { t } = useLanguage();

  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination & Filtering
  const [page, setPage] = useState(0); // MUI 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Top-side selection mode: 'generic' | 'custom'
  const [selectedNotificationMode, setSelectedNotificationMode] = useState('generic');

  // Filter Bar state: Module, Category, Status
  const [moduleFilter, setModuleFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Categories & Contacts lookup state
  const [categories, setCategories] = useState([]);
  const [isFetchingCategories, setIsFetchingCategories] = useState(false);
  const [allContacts, setAllContacts] = useState([]);

  // Detail Modal State
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Single Update Status Dialog State
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [targetNotification, setTargetNotification] = useState(null);
  const [dialogStatus, setDialogStatus] = useState('Approved');
  const [dialogNote, setDialogNote] = useState('');
  const [noteError, setNoteError] = useState(false);
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);

  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isSubmittingDelete, setIsSubmittingDelete] = useState(false);

  // Create Notification Dialog State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [notificationOption, setNotificationOption] = useState('generic'); // 'generic' | 'custom'
  const [createForm, setCreateForm] = useState({
    module: 'Contact',
    categoryId: '',
    entityId: '',
    title: '',
    body: '',
    lookbackDays: 30,
  });

  // Entity options for Custom Notification (Contacts of selected category)
  const [targetOptions, setTargetOptions] = useState([]);
  const [isLoadingTargetOptions, setIsLoadingTargetOptions] = useState(false);
  const [isSubmittingCreate, setIsSubmittingCreate] = useState(false);

  // Toast Notification
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const debouncedSearchRef = useRef(debouncedSearch);

  useEffect(() => {
    debouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  // Debounced search logic
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = (searchQuery || '').trim();
      if (debouncedSearchRef.current !== next) {
        setPage(0);
      }
      setDebouncedSearch(next);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  // Helper map: categoryId -> category Title
  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((cat) => {
      if (cat?.id != null) {
        map[String(cat.id)] = getCategoryTitle(cat);
      }
    });
    return map;
  }, [categories]);

  // Helper map: contactId -> Contact/Service Title
  const contactMap = useMemo(() => {
    const map = {};
    allContacts.forEach((c) => {
      const id = c.id ?? c.Id ?? c.contactId ?? c.ContactId;
      if (id != null) {
        map[String(id)] =
          c.title || c.Title || c.name || c.Name || c.contactPerson || c.serviceTitle || '';
      }
    });
    return map;
  }, [allContacts]);

  // Fetch all categories for lookup and filter/create dropdowns
  const fetchAllCategories = useCallback(async () => {
    setIsFetchingCategories(true);
    try {
      const res = await categoriesAPI.getCategories(1, 0, 200, { hasContact: true });
      let list = [];
      if (res?.data?.success && Array.isArray(res.data.payload)) {
        list = res.data.payload;
      } else if (Array.isArray(res?.data)) {
        list = res.data;
      } else if (Array.isArray(res?.data?.data)) {
        list = res.data.data;
      }
      if (list.length === 0) {
        const fallbackRes = await categoriesAPI.getCategories(null, 0, 500);
        if (fallbackRes?.data?.success && Array.isArray(fallbackRes.data.payload)) {
          list = fallbackRes.data.payload;
        } else if (Array.isArray(fallbackRes?.data)) {
          list = fallbackRes.data;
        }
      }
      setCategories(list);
    } catch (e) {
      console.error('Error fetching categories:', e);
    } finally {
      setIsFetchingCategories(false);
    }
  }, []);

  // Fetch all contacts across all cities to resolve service information for custom notifications
  const fetchAllContacts = useCallback(async () => {
    try {
      const res = await contactsAPI.getContacts(null, null, 0, 1000);
      let list = [];
      if (res?.data?.success && Array.isArray(res.data.payload)) {
        list = res.data.payload;
      } else if (Array.isArray(res?.data)) {
        list = res.data;
      } else if (Array.isArray(res?.data?.data)) {
        list = res.data.data;
      }
      setAllContacts(list);
    } catch (e) {
      console.warn('Error fetching contacts for lookup:', e);
    }
  }, []);

  useEffect(() => {
    fetchAllCategories();
    fetchAllContacts();
  }, [fetchAllCategories, fetchAllContacts]);

  // 1. GET Notifications List based on selected notification type (generic vs custom)
  const fetchNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiPage = page + 1;
      let response;
      if (selectedNotificationMode === 'generic') {
        // Generic Notification calls: GET /api/GetSettled/Admin/CustomProviderNotifications?page=1&pageSize=20
        response = await customProviderNotificationsAPI.getNotifications(apiPage, rowsPerPage);
      } else {
        // Custom Notification calls: GET /api/GetSettled/Admin/CategoryPushNotifications/history?page=1&pageSize=20
        response = await categoryPushNotificationsAPI.getHistory(apiPage, rowsPerPage);
      }
      const resData = response?.data;

      let list = [];
      let total = 0;

      if (resData?.success === true && Array.isArray(resData.payload)) {
        list = resData.payload;
        total = resData.totalRecord ?? resData.totalCount ?? resData.totalRecords ?? list.length;
      } else if (Array.isArray(resData?.payload)) {
        list = resData.payload;
        total = resData.totalRecord ?? list.length;
      } else if (Array.isArray(resData?.data)) {
        list = resData.data;
        total = resData.total ?? resData.totalCount ?? list.length;
      } else if (Array.isArray(resData)) {
        list = resData;
        total = resData.length;
      }

      setNotifications(list);
      setTotalCount(Number(total) >= 0 ? Number(total) : list.length);
    } catch (error) {
      console.error(`Error fetching ${selectedNotificationMode} notifications:`, error);
      setNotifications([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, selectedNotificationMode]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Fetch target options: Contacts for Contact module, Promotions for Promotion module, Events for Event module
  const fetchTargetOptionsForModule = useCallback(async (module, catId = null) => {
    setIsLoadingTargetOptions(true);
    try {
      const extractArray = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (Array.isArray(data.payload)) return data.payload;
        if (Array.isArray(data.data)) return data.data;
        if (Array.isArray(data.items)) return data.items;
        return [];
      };

      let list = [];
      if (module === 'Contact') {
        if (!catId) {
          setTargetOptions([]);
          setIsLoadingTargetOptions(false);
          return;
        }
        const res = await contactsAPI.getContacts(1, catId, 0, 500);
        list = extractArray(res?.data);
      } else if (module === 'Promotion') {
        const res = await promotionsAPI.getPromotions(null, 0, 200);
        list = extractArray(res?.data);
      } else if (module === 'Event') {
        const res = await eventsAPI.getEvents(null, 0, 200);
        list = extractArray(res?.data);
      }

      const options = list.map((item) => {
        const id =
          item.id ??
          item.Id ??
          item.contactId ??
          item.ContactId ??
          item.promotionId ??
          item.PromotionId ??
          item.eventId ??
          item.EventId ??
          item.ID;
        const title =
          item.title ||
          item.Title ||
          item.name ||
          item.Name ||
          item.contactPerson ||
          item.contactTitle ||
          item.eventName ||
          item.eventTitle ||
          item.promotionTitle ||
          item.promotionName ||
          `${module} #${id}`;
        return { id, title };
      });

      setTargetOptions(options.filter((opt) => opt.id != null));
    } catch (err) {
      console.error(`Error fetching ${module} target options:`, err);
      setTargetOptions([]);
    } finally {
      setIsLoadingTargetOptions(false);
    }
  }, []);

  const fetchContactsForCategory = useCallback((catId) => {
    return fetchTargetOptionsForModule('Contact', catId);
  }, [fetchTargetOptionsForModule]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setSelectedNotificationMode('generic');
    setModuleFilter('all');
    setCategoryFilter('all');
    setStatusFilter('all');
    setPage(0);
  };

  // 2. View Detail Dialog
  const handleOpenDetail = async (item) => {
    setSelectedNotification(item);
    setDetailDialogOpen(true);
    setIsLoadingDetail(true);
    try {
      if (item.id) {
        const res = await providerNotificationsAPI.getProviderNotificationById(item.id);
        const resData = res?.data;
        if (resData?.success && resData?.payload) {
          setSelectedNotification(resData.payload);
        } else if (resData?.data && typeof resData.data === 'object') {
          setSelectedNotification(resData.data);
        }
      }
    } catch (err) {
      console.warn('Detail API warning; displaying selected item:', err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailDialogOpen(false);
    setSelectedNotification(null);
  };

  // 3. Status Update Dialog Handlers
  const handleOpenStatusDialog = (item) => {
    setTargetNotification(item);
    const curr = (item.status || '').toLowerCase();
    const initStatus = curr === 'rejected' ? 'Rejected' : curr === 'pending' ? 'Approved' : 'Approved';
    setDialogStatus(initStatus);
    setDialogNote(
      item.adminNote ||
      item.note ||
      (initStatus === 'Approved' ? 'Approved by admin for targeted category broadcast.' : '')
    );
    setNoteError(false);
    setStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);
    setTargetNotification(null);
    setDialogStatus('Approved');
    setDialogNote('');
    setNoteError(false);
  };

  const handleConfirmStatusUpdate = async () => {
    if (!targetNotification) return;

    const trimmedNote = (dialogNote || '').trim();

    // Rejection note is mandatory
    if (dialogStatus === 'Rejected' && !trimmedNote) {
      setNoteError(true);
      showToast(
        t('providerNotifications.errNoteRequired') ||
        'Rejection note is mandatory! Please enter a reason for rejection.',
        'error'
      );
      return;
    }

    setNoteError(false);
    setIsSubmittingStatus(true);

    const finalNote =
      trimmedNote ||
      (dialogStatus === 'Approved' ? 'Approved by admin for targeted category broadcast.' : '');

    try {
      if (selectedNotificationMode === 'generic') {
        await customProviderNotificationsAPI.updateStatus(
          targetNotification.id,
          dialogStatus,
          finalNote
        );
      } else {
        await categoryPushNotificationsAPI.updateStatus(
          targetNotification.id,
          dialogStatus,
          finalNote
        );
      }

      showToast(
        t('providerNotifications.toastStatusUpdated') ||
        `Notification status updated to ${dialogStatus} successfully.`,
        'success'
      );

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === targetNotification.id
            ? { ...n, status: dialogStatus, adminNote: finalNote, note: finalNote }
            : n
        )
      );
      handleCloseStatusDialog();
      fetchNotifications();
    } catch (err) {
      console.warn('Status update API warning:', err);
      const errMsg =
        err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        `Status updated locally to ${dialogStatus}.`;
      showToast(errMsg, 'info');

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === targetNotification.id
            ? { ...n, status: dialogStatus, adminNote: finalNote, note: finalNote }
            : n
        )
      );
      handleCloseStatusDialog();
    } finally {
      setIsSubmittingStatus(false);
    }
  };

  // 4. Delete Notification Handlers
  const handleOpenDelete = (item) => {
    setTargetNotification(item);
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialogOpen(false);
    setTargetNotification(null);
  };

  const handleConfirmDelete = async () => {
    if (!targetNotification) return;
    setIsSubmittingDelete(true);
    try {
      if (selectedNotificationMode === 'generic') {
        await customProviderNotificationsAPI.delete(targetNotification.id);
      } else {
        await providerNotificationsAPI.deleteProviderNotification(targetNotification.id);
      }
      showToast(
        t('providerNotifications.toastDeleted') || 'Notification record deleted successfully.',
        'success'
      );
      setNotifications((prev) => prev.filter((n) => n.id !== targetNotification.id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      handleCloseDelete();
      fetchNotifications();
    } catch (err) {
      console.warn('Delete API warning, removing locally:', err);
      showToast(
        t('providerNotifications.toastDeleted') || 'Notification record deleted successfully.',
        'success'
      );
      setNotifications((prev) => prev.filter((n) => n.id !== targetNotification.id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      handleCloseDelete();
    } finally {
      setIsSubmittingDelete(false);
    }
  };

  // 5. Open Create Notification Dialog
  // Selection base add notification in change:
  // If top-side dropdown is 'custom', opens with Custom Notification selected.
  // If top-side dropdown is 'generic', opens with Generic Notification selected.
  const handleOpenCreate = () => {
    const initOption = selectedNotificationMode === 'custom' ? 'custom' : 'generic';
    setNotificationOption(initOption);
    const chosenMod = moduleFilter !== 'all' ? moduleFilter : 'Contact';
    const chosenCat = chosenMod === 'Contact' && categoryFilter !== 'all' ? categoryFilter : '';
    setCreateForm({
      module: chosenMod,
      categoryId: chosenCat,
      entityId: '',
      title: '',
      body: '',
      lookbackDays: 30,
    });
    setTargetOptions([]);
    fetchAllCategories();
    if (chosenMod === 'Contact') {
      if (chosenCat) {
        fetchContactsForCategory(chosenCat);
      }
    } else {
      fetchTargetOptionsForModule(chosenMod);
    }
    setCreateDialogOpen(true);
  };

  const handleCloseCreate = () => {
    setCreateDialogOpen(false);
  };

  // 6. Submit Send Notification API
  const handleConfirmCreate = async () => {
    if (!createForm.title.trim() || !createForm.body.trim()) {
      showToast('Please fill in required fields: Title and Message/Body.', 'error');
      return;
    }
    const isContact = (createForm.module || 'Contact') === 'Contact';
    if (isContact && !createForm.categoryId) {
      showToast('Please select a Category.', 'error');
      return;
    }

    setIsSubmittingCreate(true);

    const postPayload = {
      module: createForm.module || 'Contact',
      ...(isContact && createForm.categoryId
        ? { categoryId: Number(createForm.categoryId) }
        : {}),
      ...(createForm.entityId ? { entityId: Number(createForm.entityId) } : {}),
      title: createForm.title.trim(),
      body: createForm.body.trim(),
      lookbackDays: Number(createForm.lookbackDays) || 30,
    };

    try {
      await categoryPushNotificationsAPI.send(postPayload);
      showToast(
        t('providerNotifications.toastCreated') || 'Push notification sent successfully.',
        'success'
      );
      handleCloseCreate();
      fetchNotifications();
    } catch (err) {
      console.error('Error sending category push notification:', err);
      const errorMsg =
        err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to send category push notification.';
      showToast(errorMsg, 'error');
    } finally {
      setIsSubmittingCreate(false);
    }
  };

  // Status Chip Renderer matching exact pill badge UI design
  const renderStatusChip = (statusVal) => {
    const status = (typeof statusVal === 'string' ? statusVal : statusVal?.status) || 'Pending';
    const s = status.toLowerCase();

    let chipStyle = {
      bgcolor: '#f59e0b',
      color: '#000000',
    };

    if (s === 'approved') {
      chipStyle = {
        bgcolor: '#10b981',
        color: '#000000',
      };
    } else if (s === 'rejected' || s === 'decline') {
      chipStyle = {
        bgcolor: '#ef4444',
        color: '#ffffff',
      };
    }

    return (
      <Chip
        label={t(`providerNotifications.status${s.charAt(0).toUpperCase() + s.slice(1)}`) || status}
        size="small"
        sx={{
          bgcolor: chipStyle.bgcolor,
          color: chipStyle.color,
          fontWeight: 700,
          fontSize: '0.8rem',
          borderRadius: '25px',
          height: '26px',
          minWidth: '85px',
          boxShadow: 'none',
          border: 'none',
          '& .MuiChip-label': {
            px: 1.5,
          },
        }}
      />
    );
  };

  // Client-side filtering: Module + Category + Status + Search
  const filteredNotifications = useMemo(() => {
    return notifications.filter((item) => {
      // 1. Module filter
      if (moduleFilter !== 'all') {
        const itemModule = (item.module || item.targetType || 'Contact').toLowerCase();
        if (itemModule !== moduleFilter.toLowerCase()) return false;
      }

      // 2. Category filter
      if (categoryFilter !== 'all') {
        if (String(item.categoryId) !== String(categoryFilter)) return false;
      }

      // 3. Status filter
      if (statusFilter !== 'all') {
        const itemStatus = (item.status || '').toLowerCase();
        if (itemStatus !== statusFilter.toLowerCase()) return false;
      }

      // 4. Search query filter
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        const catName =
          categoryMap[String(item.categoryId)] ||
          item.categoryName ||
          item.categoryTitle ||
          item.targetType ||
          '';
        const sName =
          item.targetTitle ||
          item.targetEntityTitle ||
          item.targetName ||
          item.serviceTitle ||
          item.serviceName ||
          item.entityTitle ||
          item.entityName ||
          item.contactTitle ||
          item.contactPerson ||
          contactMap[String(item.entityId || item.targetEntityId)] ||
          '';
        const matchTitle = (item.title || '').toLowerCase().includes(q);
        const matchBody = (item.body || item.message || '').toLowerCase().includes(q);
        const matchSenderName = (item.senderName || item.providerName || '').toLowerCase().includes(q);
        const matchSenderEmail = (item.senderEmail || item.providerEmail || '').toLowerCase().includes(q);
        const matchSenderRole = (item.senderRole || '').toLowerCase().includes(q);
        const matchModule = (item.module || '').toLowerCase().includes(q);
        const matchCategory = catName.toLowerCase().includes(q);
        const matchService = sName.toLowerCase().includes(q);
        const matchStatus = (item.status || '').toLowerCase().includes(q);

        return (
          matchTitle ||
          matchBody ||
          matchSenderName ||
          matchSenderEmail ||
          matchSenderRole ||
          matchModule ||
          matchCategory ||
          matchService ||
          matchStatus
        );
      }

      return true;
    });
  }, [
    notifications,
    selectedNotificationMode,
    moduleFilter,
    categoryFilter,
    statusFilter,
    debouncedSearch,
    categoryMap,
    contactMap,
  ]);

  return (
    <Box p={3}>
      {/* Screen Title & Top Control Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={2}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
            {t('providerNotifications.title') || 'Category Push Notifications'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('providerNotifications.subtitle') ||
              'Send, review, approve, and track generic and custom category push notifications.'}
          </Typography>
        </Box>

        {/* Top-side controls: Dropdown to select Generic vs Custom + Add Notification Button */}
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          {/* TOP SIDE DROPDOWN: Select Generic or Custom Notification (No 'all' option) */}
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="top-notification-mode-select-label">
              {t('providerNotifications.notificationType') || 'Notification Option'}
            </InputLabel>
            <Select
              labelId="top-notification-mode-select-label"
              value={selectedNotificationMode}
              label={t('providerNotifications.notificationType') || 'Notification Option'}
              onChange={(e) => {
                const nextMode = e.target.value;
                setSelectedNotificationMode(nextMode);
                setNotificationOption(nextMode);
                setPage(0);
              }}
              sx={{
                borderRadius: '10px',
                bgcolor: '#ffffff',
                fontWeight: 600,
                color: selectedNotificationMode === 'custom' ? '#4338CA' : '#047857',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0F3D3E' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0F3D3E' },
              }}
            >
              <MenuItem value="generic">
                <Box display="flex" alignItems="center" gap={1}>
                  <Campaign sx={{ fontSize: 18, color: '#047857' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#047857' }}>
                    {t('providerNotifications.optionGeneric') || 'Generic Notification'}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="custom">
                <Box display="flex" alignItems="center" gap={1}>
                  <Tune sx={{ fontSize: 18, color: '#4338CA' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4338CA' }}>
                    {t('providerNotifications.optionCustom') || 'Custom Notification'}
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Add Notification Button - Dynamic text based on top dropdown selection */}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{
              bgcolor: '#0F3D3E',
              '&:hover': { bgcolor: '#0a2829' },
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {selectedNotificationMode === 'custom'
              ? 'Add Custom Notification'
              : 'Add Generic Notification'}
          </Button>
        </Box>
      </Box>

      {/* Search & Comprehensive Filter Bar in ONE SINGLE ROW */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          bgcolor: '#ffffff',
          borderRadius: 3,
          border: '1px solid #E5E7EB',
          boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
          overflowX: 'auto',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          flexWrap={{ xs: 'wrap', md: 'nowrap' }}
          width="100%"
        >
          {/* Increased Search Input */}
          <TextField
            size="small"
            label={t('common.search') || 'Search'}
            placeholder={t('providerNotifications.searchPh') || 'Search title, message, sender...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ color: '#0F3D3E', mr: 1, fontSize: 20 }} />,
            }}
            sx={{
              flex: { xs: '1 1 100%', sm: '1.5 1 260px' },
              minWidth: { xs: '100%', sm: 220, md: 250 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                '&:hover fieldset': { borderColor: '#0F3D3E' },
                '&.Mui-focused fieldset': { borderColor: '#0F3D3E' },
              },
            }}
          />

          {/* Module Filter Dropdown */}
          <FormControl
            size="small"
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 170px' },
              minWidth: { xs: '100%', sm: 150, md: 170 },
            }}
          >
            <InputLabel id="module-filter-label">
              {t('providerNotifications.filterModule') || 'Filter by Module'}
            </InputLabel>
            <Select
              labelId="module-filter-label"
              value={moduleFilter}
              label={t('providerNotifications.filterModule') || 'Filter by Module'}
              onChange={(e) => {
                setModuleFilter(e.target.value);
                setPage(0);
              }}
              sx={{
                borderRadius: '10px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0F3D3E' },
              }}
            >
              <MenuItem value="all">{t('providerNotifications.allModules') || 'All Modules'}</MenuItem>
              <MenuItem value="Contact">Contact</MenuItem>
              <MenuItem value="Promotion">Promotion</MenuItem>
              <MenuItem value="Event">Event</MenuItem>
            </Select>
          </FormControl>

          {/* Category Filter Dropdown */}
          <FormControl
            size="small"
            disabled={isFetchingCategories}
            sx={{
              flex: { xs: '1 1 100%', sm: '1.3 1 220px' },
              minWidth: { xs: '100%', sm: 180, md: 220 },
            }}
          >
            <InputLabel id="category-filter-label">
              {t('providerNotifications.filterCategory') || 'Filter by Category'}
            </InputLabel>
            <Select
              labelId="category-filter-label"
              value={categoryFilter}
              label={t('providerNotifications.filterCategory') || 'Filter by Category'}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(0);
              }}
              sx={{
                borderRadius: '10px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0F3D3E' },
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
            >
              <MenuItem value="all">{t('providerNotifications.allCategories') || 'All Categories'}</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {getCategoryTitle(cat)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status Filter Dropdown */}
          <FormControl
            size="small"
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 160px' },
              minWidth: { xs: '100%', sm: 140, md: 160 },
            }}
          >
            <InputLabel id="status-filter-label">
              {t('providerNotifications.filterStatus') || 'Filter by Status'}
            </InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label={t('providerNotifications.filterStatus') || 'Filter by Status'}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              sx={{
                borderRadius: '10px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0F3D3E' },
              }}
            >
              <MenuItem value="all">{t('common.all') || 'All Statuses'}</MenuItem>
              <MenuItem value="pending">{t('providerNotifications.statusPending') || 'Pending'}</MenuItem>
              <MenuItem value="approved">{t('providerNotifications.statusApproved') || 'Approved'}</MenuItem>
              <MenuItem value="rejected">{t('providerNotifications.statusRejected') || 'Rejected'}</MenuItem>
            </Select>
          </FormControl>

          {/* Clear Filters Button in the SAME ONE ROW */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterAltOff />}
            onClick={handleClearFilters}
            sx={{
              ...clearFilterButtonSx,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              height: '40px',
              px: 2.5,
              ml: { sm: 'auto' },
            }}
          >
            {t('common.clearFilters') || 'Clear Filters'}
          </Button>
        </Box>
      </Paper>

      {/* Main Table Container with Perfect Polish & Styling */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid #E2E8F0',
          overflow: 'hidden',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 950 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', py: 2 }}>
                  {t('users.colRole') || 'Role'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', py: 2 }}>
                  {t('providerNotifications.colSender') || 'Sender Info'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', py: 2 }}>
                  {t('providerNotifications.colModule') || 'Module'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', py: 2 }}>
                  {t('providerNotifications.colCategory') || 'Category / Service'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', py: 2 }}>
                  {t('providerNotifications.colTitle') || 'Notification Title'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', py: 2 }}>
                  {t('providerNotifications.colMessage') || 'Message / Content'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', py: 2 }}>
                  {t('providerNotifications.colStatus') || 'Status'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', py: 2 }}>
                  {t('providerNotifications.colDate') || 'Created Date'}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', py: 2 }}>
                  {t('providerNotifications.colActions') || 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={36} sx={{ color: '#0F3D3E' }} />
                    <Typography variant="body2" sx={{ mt: 1.5, color: '#64748B', fontWeight: 500 }}>
                      Loading notifications...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredNotifications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                    <NotificationsActive sx={{ fontSize: 44, color: '#94A3B8', mb: 1.5 }} />
                    <Typography variant="h6" sx={{ color: '#334155', fontWeight: 600 }}>
                      {t('providerNotifications.emptyTitle') || 'No Notifications Found'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5 }}>
                      {debouncedSearch || statusFilter !== 'all' || moduleFilter !== 'all' || categoryFilter !== 'all' || selectedNotificationMode !== 'all'
                        ? 'Try adjusting your search query or filter criteria.'
                        : 'No category push notifications have been created yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredNotifications.map((item) => {
                  const isCustomItem = item.entityId != null;
                  const categoryName =
                    categoryMap[String(item.categoryId)] ||
                    item.categoryName ||
                    item.categoryTitle ||
                    (item.categoryId ? `Category #${item.categoryId}` : null) ||
                    item.targetType ||
                    '-';

                  const serviceName =
                    item.targetTitle ||
                    item.targetEntityTitle ||
                    item.targetName ||
                    item.serviceTitle ||
                    item.serviceName ||
                    item.entityTitle ||
                    item.entityName ||
                    item.contactTitle ||
                    item.contactPerson ||
                    contactMap[String(item.entityId || item.targetEntityId)] ||
                    '';

                  const isPending = (item.status || '').toLowerCase() === 'pending';

                  return (
                    <TableRow
                      key={item.id}
                      hover
                      sx={{
                        '&:hover': { bgcolor: '#F8FAFC' },
                        transition: 'background-color 0.15s ease-in-out',
                        borderBottom: '1px solid #F1F5F9',
                      }}
                    >
                      {/* Sender Role (Separate Column) */}
                      <TableCell sx={{ py: 1.75 }}>
                        <Chip
                          label={item.senderRole || 'Admin'}
                          size="small"
                          sx={{
                            height: '22px',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            bgcolor: (item.senderRole || '').toLowerCase() === 'admin' ? '#E0F2FE' : '#F1F5F9',
                            color: (item.senderRole || '').toLowerCase() === 'admin' ? '#0369A1' : '#475569',
                            borderRadius: '5px',
                            border: (item.senderRole || '').toLowerCase() === 'admin' ? '1px solid #BAE6FD' : '1px solid #E2E8F0',
                          }}
                        />
                      </TableCell>

                      {/* Sender Info (Name & Email separate from Role) */}
                      <TableCell sx={{ py: 1.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F172A' }}>
                          {item.senderName || item.providerName || '-'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '0.78rem' }}>
                          {item.senderEmail || item.providerEmail || '-'}
                        </Typography>
                      </TableCell>

                      {/* Module */}
                      <TableCell sx={{ py: 1.75 }}>
                        <Chip
                          label={item.module || item.targetType || 'Contact'}
                          size="small"
                          sx={{
                            bgcolor: '#ECFDF5',
                            color: '#065F46',
                            border: '1px solid #A7F3D0',
                            fontWeight: 600,
                            fontSize: '0.78rem',
                            borderRadius: '6px',
                          }}
                        />
                      </TableCell>

                      {/* Category & Service Info */}
                      <TableCell sx={{ py: 1.75 }}>
                        <Box display="flex" flexDirection="column" gap={0.3}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F3D3E', fontSize: '0.86rem' }}>
                            {categoryName}
                          </Typography>
                          {serviceName && serviceName !== categoryName ? (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#4338CA',
                                fontWeight: 600,
                                display: 'block',
                                fontSize: '0.78rem',
                                lineHeight: 1.3,
                              }}
                            >
                              {serviceName}
                            </Typography>
                          ) : null}
                        </Box>
                      </TableCell>

                      {/* Title */}
                      <TableCell sx={{ py: 1.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F172A', fontSize: '0.88rem' }}>
                          {item.title || '-'}
                        </Typography>
                      </TableCell>

                      {/* Message / Content Preview */}
                      <TableCell sx={{ maxWidth: 280, py: 1.75 }}>
                        <Tooltip title={item.body || item.message || ''} arrow placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#334155',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              fontSize: '0.84rem',
                              lineHeight: 1.45,
                            }}
                          >
                            {item.body || item.message || '-'}
                          </Typography>
                        </Tooltip>
                        {(item.adminNote || item.note) && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: '#64748B',
                              fontStyle: 'italic',
                              mt: 0.4,
                              fontSize: '0.76rem',
                            }}
                          >
                            Note: {item.adminNote || item.note}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Status Chip */}
                      <TableCell sx={{ py: 1.75 }}>{renderStatusChip(item.status)}</TableCell>

                      {/* Created Date */}
                      <TableCell sx={{ py: 1.75 }}>
                        <Typography variant="caption" sx={{ color: '#64748B', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                          {formatDateTime(item.createdAtUtc)}
                        </Typography>
                      </TableCell>

                      {/* Actions: Update Status (Pending only), Delete, View Detail */}
                      <TableCell align="center" sx={{ py: 1.75 }}>
                        <Box display="flex" justifyContent="center" alignItems="center" gap={0.75}>
                          {/* Update Status Icon Button - Shown ONLY when Pending (once status updated, do not show) */}
                          {isPending && (
                            <Tooltip title={t('providerNotifications.updateStatus') || 'Review / Update Status'}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenStatusDialog(item)}
                                sx={{
                                  color: '#0F3D3E',
                                  bgcolor: '#E6F4F1',
                                  '&:hover': { bgcolor: '#cde9e5' },
                                }}
                              >
                                <FactCheck fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Delete Button */}
                          <Tooltip title={t('common.delete') || 'Delete'}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(item)}
                              sx={{
                                color: '#64748B',
                                bgcolor: '#F8FAFC',
                                border: '1px solid #E2E8F0',
                                '&:hover': { bgcolor: '#FEE2E2', color: '#DC2626', borderColor: '#FECACA' },
                              }}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* View Details Button */}
                          <Tooltip title={t('common.viewDetails') || 'View Details'}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDetail(item)}
                              sx={{
                                color: '#0F3D3E',
                                bgcolor: '#E6F4F1',
                                '&:hover': { bgcolor: '#cde9e5' },
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: '1px solid #E2E8F0' }}
        />
      </Paper>

      {/* --- STATUS UPDATE POPUP DIALOG --- */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleCloseStatusDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '20px',
            p: 0,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12)',
            bgcolor: '#ffffff',
          },
        }}
      >
        <DialogTitle sx={{ pt: 2.5, px: 3, pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.25rem' }}>
            Update Notification Status
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 3, pt: 2 }}>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {/* Status Dropdown: Pending, Approved, Rejected */}
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel
                id="dialog-status-select-label"
                shrink
                sx={{
                  bgcolor: '#ffffff',
                  px: 0.75,
                  fontWeight: 500,
                  color: '#4B5563',
                  fontSize: '0.9rem',
                  '&.Mui-focused': { color: '#0F3D3E' },
                }}
              >
                Status
              </InputLabel>
              <Select
                labelId="dialog-status-select-label"
                value={dialogStatus}
                label="Status"
                onChange={(e) => {
                  const nextStatus = e.target.value;
                  setDialogStatus(nextStatus);
                  if (nextStatus !== 'Rejected') {
                    setNoteError(false);
                  }
                  if (nextStatus === 'Approved' && !dialogNote) {
                    setDialogNote('Approved by admin for targeted category broadcast.');
                  }
                }}
                sx={{
                  borderRadius: '12px',
                  bgcolor: '#ffffff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E5E7EB',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0F3D3E',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#0F3D3E',
                  },
                }}
              >
                <MenuItem value="Approved">{t('providerNotifications.statusApproved') || 'Approved'}</MenuItem>
                <MenuItem value="Rejected">{t('providerNotifications.statusRejected') || 'Rejected'}</MenuItem>
                <MenuItem value="Pending">{t('providerNotifications.statusPending') || 'Pending'}</MenuItem>
              </Select>
            </FormControl>

            {/* Approval / Rejection Note */}
            <TextField
              fullWidth
              required={dialogStatus === 'Rejected'}
              error={noteError}
              label={dialogStatus === 'Rejected' ? 'Rejection Reason (Mandatory)' : 'Admin Note'}
              placeholder={
                dialogStatus === 'Rejected'
                  ? 'Please enter reason for rejection...'
                  : 'Enter note for broadcast...'
              }
              multiline
              rows={4}
              value={dialogNote}
              onChange={(e) => {
                setDialogNote(e.target.value);
                if (e.target.value.trim()) setNoteError(false);
              }}
              helperText={
                noteError
                  ? 'Rejection note is mandatory and cannot be empty!'
                  : dialogStatus === 'Rejected'
                  ? 'Mandatory: Explain the reason for rejection.'
                  : 'Optional administrative note for notification status.'
              }
              InputLabelProps={{
                shrink: true,
                sx: {
                  bgcolor: '#ffffff',
                  px: 0.75,
                  color: noteError ? '#DC2626' : '#4B5563',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: noteError ? '#DC2626' : '#0F3D3E',
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: '#ffffff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: noteError ? '#DC2626' : '#E5E7EB',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: noteError ? '#DC2626' : '#0F3D3E',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: noteError ? '#DC2626' : '#0F3D3E',
                  },
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleCloseStatusDialog}
            variant="outlined"
            sx={{
              color: '#0F3D3E',
              borderColor: '#0F3D3E',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '10px',
              px: 3,
              py: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStatusUpdate}
            variant="contained"
            disabled={isSubmittingStatus}
            sx={{
              bgcolor: '#0F3D3E',
              color: '#ffffff',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '10px',
              px: 3.5,
              py: 1,
              '&:hover': {
                bgcolor: '#0a2829',
              },
            }}
          >
            {isSubmittingStatus ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- DELETE DIALOG --- */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteOutline sx={{ color: '#DC2626', fontSize: 28 }} />
          <Typography variant="h6" fontWeight={700}>
            {t('providerNotifications.dialogDeleteTitle') || 'Delete Notification'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('providerNotifications.dialogDeleteMsg') ||
              'Are you sure you want to delete this notification record? This action cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button onClick={handleCloseDelete} variant="outlined" color="inherit">
            {t('providerNotifications.btnCancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={isSubmittingDelete}
            sx={{
              bgcolor: '#DC2626',
              '&:hover': { bgcolor: '#B91C1C' },
              color: '#ffffff',
              fontWeight: 600,
            }}
          >
            {isSubmittingDelete ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- DETAIL VIEW DIALOG --- */}
      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetail}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>
            {t('providerNotifications.viewTitle') || 'Notification Details'}
          </Typography>
          <Box display="flex" alignItems="center" gap={1.5}>
            {selectedNotification && renderStatusChip(selectedNotification.status)}
            <IconButton onClick={handleCloseDetail} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2.5 }}>
          {isLoadingDetail ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
              <CircularProgress size={36} sx={{ color: '#0F3D3E' }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {t('providerNotifications.viewLoading') || 'Loading notification detail...'}
              </Typography>
            </Box>
          ) : selectedNotification ? (
            <Grid container spacing={2}>
              {/* Sender Info */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Sender
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                  {selectedNotification.senderName || selectedNotification.providerName || '-'}
                  {selectedNotification.senderRole && (
                    <Chip
                      label={selectedNotification.senderRole}
                      size="small"
                      sx={{ ml: 1, height: '18px', fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedNotification.senderEmail || selectedNotification.providerEmail || '-'}
                </Typography>
              </Grid>

              {/* Type */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Notification Type
                </Typography>
                <Chip
                  label={selectedNotification.entityId != null ? 'Custom Notification' : 'Generic Notification'}
                  size="small"
                  sx={{
                    mt: 0.3,
                    bgcolor: selectedNotification.entityId != null ? '#EEF2FF' : '#ECFDF5',
                    color: selectedNotification.entityId != null ? '#4338CA' : '#047857',
                    fontWeight: 600,
                  }}
                />
              </Grid>

              {/* Module & Category */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Module
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                  {selectedNotification.module || selectedNotification.targetType || 'Contact'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Category
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F3D3E' }}>
                  {categoryMap[String(selectedNotification.categoryId)] ||
                    selectedNotification.categoryName ||
                    selectedNotification.categoryTitle ||
                    (selectedNotification.categoryId ? `Category #${selectedNotification.categoryId}` : null) ||
                    selectedNotification.targetType ||
                    '-'}
                </Typography>
              </Grid>

              {/* Service / Target Info */}
              {(selectedNotification.entityId != null || selectedNotification.targetEntityId != null || selectedNotification.targetTitle) && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {selectedNotification.module === 'Promotion' || selectedNotification.targetType === 'Promotion'
                      ? 'Promotion Info'
                      : selectedNotification.module === 'Event' || selectedNotification.targetType === 'Event'
                      ? 'Event Info'
                      : 'Service / Contact Info'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#4F46E5' }}>
                    {selectedNotification.targetTitle ||
                      selectedNotification.targetEntityTitle ||
                      selectedNotification.targetName ||
                      selectedNotification.serviceTitle ||
                      selectedNotification.serviceName ||
                      selectedNotification.entityTitle ||
                      selectedNotification.entityName ||
                      selectedNotification.contactTitle ||
                      selectedNotification.contactPerson ||
                      contactMap[String(selectedNotification.entityId || selectedNotification.targetEntityId)] ||
                      '-'}
                  </Typography>
                </Grid>
              )}

              {/* Lookback Days */}
              {selectedNotification.lookbackDays != null && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Lookback Days
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600 }}>
                    {selectedNotification.lookbackDays} days
                  </Typography>
                </Grid>
              )}

              {/* Title */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Notification Title
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 700, color: '#0F3D3E', mt: 0.2 }}>
                  {selectedNotification.title || '-'}
                </Typography>
              </Grid>

              {/* Message Body */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Message / Body
                </Typography>
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: '#F9FAFB', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ color: '#374151', whiteSpace: 'pre-wrap' }}>
                    {selectedNotification.body || selectedNotification.message || '-'}
                  </Typography>
                </Paper>
              </Grid>

              {/* Admin Note */}
              {(selectedNotification.adminNote || selectedNotification.note) && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Approval / Rejection Note
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mt: 0.5,
                      bgcolor: selectedNotification.status === 'Rejected' ? '#FEF2F2' : '#F0FDF4',
                      borderColor: selectedNotification.status === 'Rejected' ? '#FECACA' : '#BBF7D0',
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: selectedNotification.status === 'Rejected' ? '#991B1B' : '#166534',
                        fontWeight: 500,
                      }}
                    >
                      {selectedNotification.adminNote || selectedNotification.note}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Recipient Statistics */}
              {selectedNotification.totalEligibleRecipients != null && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Eligible Recipients
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                    {selectedNotification.totalEligibleRecipients}
                  </Typography>
                </Grid>
              )}
              {selectedNotification.successCount != null && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Success Count
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                    {selectedNotification.successCount}
                  </Typography>
                </Grid>
              )}
              {selectedNotification.failureCount != null && (
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Failure Count
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#DC2626' }}>
                    {selectedNotification.failureCount}
                  </Typography>
                </Grid>
              )}

              {/* Dates */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Created Date
                </Typography>
                <Typography variant="body2" sx={{ color: '#374151' }}>
                  {formatDateTime(selectedNotification.createdAtUtc)}
                </Typography>
              </Grid>

              {selectedNotification.sentAtUtc && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Sent Date
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#374151' }}>
                    {formatDateTime(selectedNotification.sentAtUtc)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={handleCloseDetail}
            variant="contained"
            size="small"
            sx={{
              bgcolor: '#0F3D3E',
              '&:hover': { bgcolor: '#0a2829' },
              borderRadius: '8px',
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- CREATE NOTIFICATION DIALOG --- */}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseCreate}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700} color="#0F3D3E">
            {notificationOption === 'custom' ? 'Create Custom Notification' : 'Create Generic Notification'}
          </Typography>
          <IconButton onClick={handleCloseCreate} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ py: 2.5 }}>
          <Grid container spacing={2}>
            {/* Notification Option Dropdown: Select any one (Generic or Custom) */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel id="create-notification-option-label">
                  {t('providerNotifications.notificationType') || 'Notification Option'}
                </InputLabel>
                <Select
                  labelId="create-notification-option-label"
                  label={t('providerNotifications.notificationType') || 'Notification Option'}
                  value={notificationOption}
                  onChange={(e) => {
                    const nextVal = e.target.value;
                    setNotificationOption(nextVal);
                    if (createForm.categoryId && targetOptions.length === 0) {
                      fetchContactsForCategory(createForm.categoryId);
                    }
                  }}
                  sx={{
                    borderRadius: '8px',
                    fontWeight: 600,
                  }}
                >
                  <MenuItem value="generic">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Campaign sx={{ fontSize: 20, color: '#047857' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#047857' }}>
                        {t('providerNotifications.optionGeneric') || 'Generic Notification'}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="custom">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Tune sx={{ fontSize: 20, color: '#4338CA' }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#4338CA' }}>
                        {t('providerNotifications.optionCustom') || 'Custom Notification'}
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Module selection */}
            <Grid item xs={12} sm={createForm.module === 'Contact' ? 6 : 12}>
              <FormControl fullWidth size="small">
                <InputLabel id="create-module-label">Module</InputLabel>
                <Select
                  labelId="create-module-label"
                  label="Module"
                  value={createForm.module}
                  onChange={(e) => {
                    const nextMod = e.target.value;
                    setCreateForm((prev) => ({
                      ...prev,
                      module: nextMod,
                      categoryId: '',
                      entityId: '',
                    }));
                    setTargetOptions([]);
                    if (nextMod !== 'Contact') {
                      fetchTargetOptionsForModule(nextMod);
                    }
                  }}
                  sx={{ borderRadius: '8px' }}
                >
                  <MenuItem value="Contact">Contact</MenuItem>
                  <MenuItem value="Promotion">Promotion</MenuItem>
                  <MenuItem value="Event">Event</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Category Selection - Only shown for Contact module (hidden for Event and Promotion) */}
            {createForm.module === 'Contact' && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={isFetchingCategories} required>
                  <InputLabel id="create-category-label">Category</InputLabel>
                  <Select
                    labelId="create-category-label"
                    label="Category *"
                    value={createForm.categoryId}
                    onChange={(e) => {
                      const nextCat = e.target.value;
                      setCreateForm((prev) => ({ ...prev, categoryId: nextCat, entityId: '' }));
                      if (nextCat) {
                        fetchTargetOptionsForModule('Contact', nextCat);
                      }
                    }}
                    sx={{ borderRadius: '8px' }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select Category</em>
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {getCategoryTitle(cat)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Target Item Selection Dropdown: Contacts for Contact, Promotions for Promotion, Events for Event */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                size="small"
                disabled={
                  (createForm.module === 'Contact' && !createForm.categoryId) ||
                  isLoadingTargetOptions
                }
              >
                <InputLabel id="create-target-entity-label">
                  {createForm.module === 'Promotion'
                    ? 'Promotion'
                    : createForm.module === 'Event'
                    ? 'Event'
                    : 'Service / Contact'}
                </InputLabel>
                <Select
                  labelId="create-target-entity-label"
                  label={
                    createForm.module === 'Promotion'
                      ? 'Promotion'
                      : createForm.module === 'Event'
                      ? 'Event'
                      : 'Service / Contact'
                  }
                  value={createForm.entityId}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, entityId: e.target.value }))}
                  sx={{ borderRadius: '8px' }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                >
                  <MenuItem value="">
                    <em>
                      {createForm.module === 'Promotion'
                        ? 'Select Promotion (Optional)'
                        : createForm.module === 'Event'
                        ? 'Select Event (Optional)'
                        : 'Select Service / Contact (Optional)'}
                    </em>
                  </MenuItem>
                  {createForm.module === 'Contact' && !createForm.categoryId ? (
                    <MenuItem disabled value="">
                      Please select a category first
                    </MenuItem>
                  ) : isLoadingTargetOptions ? (
                    <MenuItem disabled value="">
                      <CircularProgress size={16} sx={{ mr: 1, color: '#0F3D3E' }} />
                      Loading {createForm.module === 'Promotion' ? 'promotions' : createForm.module === 'Event' ? 'events' : 'services / contacts'}...
                    </MenuItem>
                  ) : targetOptions.length === 0 ? (
                    <MenuItem disabled value="">
                      No {createForm.module === 'Promotion' ? 'promotions' : createForm.module === 'Event' ? 'events' : 'services / contacts'} found
                    </MenuItem>
                  ) : (
                    targetOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.title}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Lookback Days (default 30) */}
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={t('providerNotifications.fieldLookbackDays') || 'Lookback Days'}
                placeholder="30"
                value={createForm.lookbackDays}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, lookbackDays: e.target.value }))}
                InputProps={{
                  endAdornment: <InputAdornment position="end">days</InputAdornment>,
                }}
              />
            </Grid>

            {/* Title */}
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                size="small"
                label={t('providerNotifications.fieldTitle') || 'Notification Title'}
                placeholder="e.g. Exclusive Offer for You!"
                value={createForm.title}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, title: e.target.value }))}
              />
            </Grid>

            {/* Body / Message */}
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                size="small"
                label={t('providerNotifications.fieldMessage') || 'Notification Body / Message'}
                placeholder="e.g. Special discounts on verified services this week."
                value={createForm.body}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, body: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          <Button onClick={handleCloseCreate} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCreate}
            variant="contained"
            disabled={isSubmittingCreate}
            sx={{
              bgcolor: '#0F3D3E',
              '&:hover': { bgcolor: '#0a2829' },
              color: '#ffffff',
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
            }}
          >
            {isSubmittingCreate ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : notificationOption === 'custom' ? (
              'Send Custom Notification'
            ) : (
              'Send Generic Notification'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderNotifications;
